import { useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import {
  EArchType,
  EShowArchType,
  stageControl,
  stagingManager,
  viewControlInTreatment,
  wasmModule,
} from "@/gluelayer"
import { spaceManagement } from "@/gluelayer/core/modules/spaceManagement"
import MyDialog from "@/shared/components/MyDialog"
import { IPRDistributionList } from "@/shared/data/ud-treat"
import { EIPRNtoNType, ETopLeftViewType } from "@/shared/types"
import utils from "@/shared/utils"
import { isRetainerArch } from "@/shared/utils/tools/caseUtils"
import {
  setCurArch,
  setOpenCollisionCheck,
  setTopLeftViewType,
} from "@/UDTreat/udTreatSlice"
import { Button, Tooltip } from "@mui/material"
import { FC, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"

const { setStageCallback, setStageStep, getWasmStageData } = stageControl

interface IProps {
  curArch: string
  setPageLoading: (bool: boolean) => void
}
const IPRSpaceManagement: FC<IProps> = ({ curArch, setPageLoading }) => {
  const dispatch = useDispatch()
  const { openCollisionCheck } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const [type, setType] = useState<EIPRNtoNType>()
  const [isShowFinalStageModel, setIsShowFinalStageModel] = useState(false)
  const [isAutoCorrectionMidlineModel, setIsAutoCorrectionMidlineModel] =
    useState(false)
  const [isPassiveAligner, setIsPassiveAligner] = useState(false)
  const [data, setData] = useState<{
    UpData: string
    LowData: string
  }>({
    UpData: "0",
    LowData: "0",
  })

  /**
   * Init Data
   */
  const getTotalIPRValue = () => {
    const iprValue = spaceManagement.GetTotalIPRValue()
    if (iprValue) {
      if (!iprValue.size()) return
      const arr = [data.UpData, data.LowData]
      for (let i = 0; i < iprValue.size(); i++) {
        arr[i] = iprValue.get(i)
      }
      setData({
        UpData: arr[0],
        LowData: arr[1],
      })
    }
  }

  const initView = async (isInit: boolean) => {
    if (!isInit) {
      spaceManagement.SwitchModuleSpaceManagement(false)
      wasmModule.moduleManager.SwitchModuleOrientationAdjustment(false)
      // await dispatch(setOpenCollisionCheck(true))
      await utils.sleep(50)
      const iprModule = wasmModule.moduleManager.GetIPRModule()
      if (iprModule) iprModule.ChangeShowZeroMode(false)
      dispatch(setTopLeftViewType(ETopLeftViewType.FRONT))
      viewControlInTreatment.setViewType(ETopLeftViewType.FRONT)

      return
    }

    if (curArch === "all") {
      const upIsRetainer = isRetainerArch(EArchType.UpArch)
      if (upIsRetainer) {
        dispatch(setCurArch("low"))
      } else {
        dispatch(setCurArch("up"))
      }
    }
    if (!openCollisionCheck) {
      dispatch(setOpenCollisionCheck(true))
    }
    await utils.sleep(50)
    const iprModule = wasmModule.moduleManager.GetIPRModule()
    if (iprModule) iprModule.ChangeShowZeroMode(true)
    dispatch(setTopLeftViewType(ETopLeftViewType.SPLIT))
    viewControlInTreatment.setViewType(ETopLeftViewType.SPLIT)

    const isOpen = spaceManagement.GetModuleSpaceManagement()
    if (!isOpen) {
      spaceManagement.SwitchModuleSpaceManagement(true)
    }
    // getTotalIPRValue()
    // spaceManagement.SetUpdateTotalIPRCallback(
    //   (UpData: string, LowData: string) => {
    //     setData({ UpData, LowData })
    //   },
    // )
  }

  useEffect(() => {
    initView(true)
    return () => {
      initView(false)
    }
  }, [])

  const toothType = useMemo(() => {
    const type =
      curArch === "up"
        ? EShowArchType.UpArch
        : curArch === "low"
        ? EShowArchType.LowArch
        : EShowArchType.BothArch
    return type
  }, [curArch])

  /**
   *  verify Stage
   */
  const verifyStage = () => {
    const GMData = getWasmStageData()
    if (GMData) {
      const upStage = GMData.jointUpKeypoints.length > 0
      const lowStage = GMData.jointLowerKeypoints.length > 0
      if (curArch === "up") {
        return upStage
      }
      if (curArch === "low") {
        return lowStage
      }
      return upStage || lowStage
    } else {
      return false
    }
  }

  /**
   *  verify passive aligner Dialog
   */
  const verifyPassiveAligner = () => {
    const containsPA = stagingManager.isStageContainsPA(toothType)
    if (containsPA) {
      setIsPassiveAligner(true)
      return true
    } else {
      return false
    }
  }

  /**
   *  verify Final Stage Dialog
   */
  const verifyIsFinalStage = () => {
    const isFinalStage = spaceManagement.IsFinalStage(toothType)
    if (!isFinalStage) {
      setIsShowFinalStageModel(true)
      return true
    } else {
      return false
    }
  }

  /**
   * IPR Distribution
   */
  const selectIPRDistribution = (isMidLine: boolean, type: EIPRNtoNType) => {
    const isOpen = spaceManagement.GetModuleSpaceManagement()
    if (isOpen) {
      spaceManagement.SetIPRNtoN(type, isMidLine)
    } else {
      spaceManagement.SwitchModuleSpaceManagement(true)
      spaceManagement.SetIPRNtoN(type, isMidLine)
    }
  }

  /**
   * Space Management
   */
  const handleSpaceManagement = async (bool: boolean) => {
    if (!bool) return
    setPageLoading(true)
    await utils.sleep(50)
    // UpArch = 0,LowArch = 1, BothArch = 2,
    // up, low, all
    spaceManagement.SetResolveCollision(toothType, () => {
      setPageLoading(false)
    })
  }

  /**
   * Auto Correction Midline Model
   */
  const handleAutoCorrectionMidline = () => {
    const isPassiveAligner = verifyPassiveAligner()
    if (isPassiveAligner) return
    const isFinalStage = verifyIsFinalStage()
    if (isFinalStage) return
    handleSpaceManagement(true)
    // const isMidLine = spaceManagement.IsWillShift(toothType)
    // if (isMidLine) {
    //   setIsAutoCorrectionMidlineModel(true)
    // } else {
    //   handleSpaceManagement(true)
    // }
  }

  return (
    <div className="card-item-body">
      {/* <div className="total-ipr-value">
        <div className="title"> Total IPR Value: </div>
        <div className="value">
          <div>
            <span>U</span>: {data.UpData || 0} mm
          </div>
          <div>
            <span>L</span>: {data.LowData || 0} mm
          </div>
        </div>
      </div> */}
      <h3 className="card-item-sub-title">Distribute</h3>
      <div className="content-space mb-15">
        {IPRDistributionList.map((item) => (
          <Button
            key={item.value}
            className="btn w-100 btn-secondary my-btn-secondary"
            variant="contained"
            onClick={() => {
              // 校验是否有调整计划
              const ishaveStage = verifyStage()
              if (!ishaveStage) return
              const isPassiveAligner = verifyPassiveAligner()
              if (isPassiveAligner) return
              const isFinalStage = verifyIsFinalStage()
              if (isFinalStage) return
              setType(item.value as EIPRNtoNType)
              selectIPRDistribution(false, item.value as EIPRNtoNType) // default false for do not keep midline
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>
      {/* <h3 className="card-item-sub-title">Space Mgmt.</h3> */}
      {/* <h3 className="card-item-sub-title">Space Management</h3> */}
      <Tooltip title="Resolve 0.5mm space/collision per click" placement="top">
        <Button
          className="btn w-100 btn-secondary my-btn-secondary"
          variant="contained"
          onClick={handleAutoCorrectionMidline}
        >
          Auto Correction
        </Button>
      </Tooltip>

      {/* <MyDialog
        open={isAutoCorrectionMidlineModel}
        title="Warning"
        content="This adjustment could change your midline slightly. Would you like to proceed? "
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-primary"
              variant="contained"
              onClick={() => handleSpaceManagement(true)}
            >
              Yes
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => handleSpaceManagement(false)}
            >
              No
            </Button>
          </div>
        }
      /> */}

      {/*final stage*/}
      <MyDialog
        open={isShowFinalStageModel}
        title="Warning"
        content="This function only works on the final stage."
        cancelText="OK"
        onClose={() => {
          stagingManager.SetStageToFinal(EShowArchType.BothArch)
          setIsShowFinalStageModel(false)
        }}
      />

      {/*Changes to the tooth movement plan will require new staging of passive aligner.*/}
      <MyDialog
        open={isPassiveAligner}
        title="Warning"
        content="Changes to the tooth movement plan will require new staging of passive aligner."
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-primary"
              variant="contained"
              onClick={() => {
                stagingManager.delPassiveAligner(toothType)
                setIsPassiveAligner(false)
              }}
            >
              Confirm
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => setIsPassiveAligner(false)}
            >
              Cancel
            </Button>
          </div>
        }
      />
    </div>
  )
}

export default IPRSpaceManagement
